/**
 * Global state when the API is temporarily unreachable (502/503/deploy restart).
 * Drives the top banner and lets callers suppress noisy error toasts/logs.
 */

const RETRY_MS = 3000;
const listeners = new Set();

let active = false;
let message = 'Server is restarting. Please wait a few seconds and retry.';
let retryTimer = null;

const notify = () => {
  listeners.forEach((fn) => {
    try { fn({ active, message }); } catch (_) { /* no-op */ }
  });
};

export const isServerUnavailable = () => active;

export const getServerUnavailableMessage = () => message;

export const subscribeServerUnavailable = (fn) => {
  listeners.add(fn);
  fn({ active, message });
  return () => listeners.delete(fn);
};

export const activateServerUnavailable = (detail) => {
  const nextMessage = detail?.message || message;
  const changed = !active || nextMessage !== message;
  active = true;
  message = nextMessage;
  if (changed) notify();
  scheduleHealthPoll();
};

export const clearServerUnavailable = () => {
  if (!active) return;
  active = false;
  notify();
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
};

const scheduleHealthPoll = () => {
  if (retryTimer) return;
  retryTimer = setTimeout(async () => {
    retryTimer = null;
    if (!active) return;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/health`, { cache: 'no-store' });
      if (res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body?.status === 'ok') {
          clearServerUnavailable();
          return;
        }
      }
    } catch (_) {
      /* still down */
    }
    scheduleHealthPoll();
  }, RETRY_MS);
};

export const isServerUnavailableError = (error) => {
  if (!error) return false;
  if (error.isServerUnavailable) return true;
  const status = error.response?.status;
  if (status === 502 || status === 503) return true;
  if (!error.response && (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error'))) {
    return active;
  }
  return false;
};

export const logApiError = (context, error) => {
  if (isServerUnavailableError(error)) return;
  console.error(context, error);
};
